import dayjs, { Dayjs } from "utils/dayjs"

import i18n from "intl/i18n"
import { Guid, IApiLink, Lmid } from "@/types"
import { ApiLinkEntity, ApiLinkRel, Currency, EditSessionConfirmModalDuration, EditSessionDuration, ErrorCode, Purpose } from "config/constants"
import ReservationInvoiceInvoicePosition, { IGetReservationInvoiceInvoicePositionDto } from "./ReservationInvoiceInvoicePosition"
import CustomError from "./CustomError"
import alertModals from "stores/alertModals"
import toastMessages from "stores/toastMessages"
import { ICreateInvoiceAndNoteConflictErrorPayload } from "services/AssetService"
import { IAddressDto } from "./Address"
import { ICustomerContactDto } from "./CustomerContact"
import InvoiceDraftAsset, { IGetInvoiceDraftAssetDto } from "./InvoiceDraftAsset"
import DowntimePeriod, { IGetDowntimePeriodDto } from "./DowntimePeriod"
import TenantInvoicePosition from "./TenantInvoicePosition"


export enum OutgoingInvoiceType {
    invoice = "invoice",
    fullCreditNote = "fullCreditNote",
    partialCreditNote = "partialCreditNote",
    partialDebitNote = "partialDebitNote",
    collectiveCreditNote = "collectiveCreditNote",
}

export enum OutgoingInvoiceStatus {
    pendingErpSend = "pendingErpSend",
    awaitImport = "awaitImport",
    orderCreated = "orderCreated",
    done = "done",
    error = "error",
}

export enum OutgoingInvoiceCreditType {
    partiallyCredited = "partiallyCredited",
    canceled = "canceled",
}

export enum InvoiceRateCalculationType {
    flatRate = "flatRate",
    prorated = "prorated",
}

export interface ICreatePartialCreditNoteDto {
    headerText: string
    footerText: string
    internalComment: string
    sendToErp?: boolean
    items: {
        invoicePositionId: Guid
        amount: number
    }[]
}

export interface ICreateFullCreditNoteDto {
    headerText?: string | null
    footerText?: string | null
    internalComment?: string | null
    sendToErp?: boolean
}

export interface ICreateCollectiveCreditNoteDto {
    reservationId: Guid
    invoiceIds: Guid[]
    headerText?: string | null
    footerText?: string | null
    internalComment: string | null
    sendToErp?: boolean
}

export enum ErpInvoiceStatus {
    received = "received",
    pending = "pending",
    error = "error",
}

export enum OutgoingInvoiceSortBy {
    requestNumber = "requestNumber",
    purpose = "purpose",
    start = "start",
    end = "end",
    type = "type",
    status = "status",
    article = "article",
    lmid = "lmid",
    createdOn = "createdOn",
    customerNumber = "customerNumber",
    customer = "customer",
    orderNumber = "orderNumber",
    invoiceNumber = "invoiceNumber",
    invoiceDate = "invoiceDate",
    disposalDate = "disposalDate",
}


export interface IOutgoingInvoiceCustomer {
    name: string
    number?: string | null
    address?: IAddressDto | null
    customerContact?: ICustomerContactDto | null
    blocked: boolean
    currency?: Currency | null
}


// OutgoingInvoiceBase

export interface IOutgoingInvoiceBaseDto {
    id: Guid
    start: string
    end?: string | null
    amount: number
    status: OutgoingInvoiceStatus
    type: OutgoingInvoiceType
    creditType?: OutgoingInvoiceCreditType | null
    erpOrderNumber?: string | null
    erpOrderNumberStatus?: ErpInvoiceStatus | null
    erpOrderNumberMessage?: string | null
    erpInvoiceNumber?: string | null
    erpInvoiceNumberStatus?: ErpInvoiceStatus | null
    erpInvoiceDate?: string | null
    erpInvoiceDateStatus?: ErpInvoiceStatus | null
    internalComment?: string | null
    customer: IOutgoingInvoiceCustomer
    createdAutomatically: boolean
    isAdHoc: boolean
    costUnit?: string | null
    created: string
    createdBy: Guid
}

export class OutgoingInvoiceBase {
    id: Guid
    start: Dayjs
    end?: Dayjs
    amount: number
    status: OutgoingInvoiceStatus
    type: OutgoingInvoiceType
    creditType?: OutgoingInvoiceCreditType
    erpOrderNumber?: string
    erpOrderNumberStatus?: ErpInvoiceStatus
    erpOrderNumberMessage?: string
    erpInvoiceNumber?: string
    erpInvoiceNumberStatus?: ErpInvoiceStatus
    erpInvoiceDate?: Dayjs
    erpInvoiceDateStatus?: ErpInvoiceStatus
    internalComment?: string
    customer: IOutgoingInvoiceCustomer
    createdAutomatically: boolean
    isAdHoc: boolean
    costUnit?: string
    created: Dayjs
    createdBy: Guid

    constructor(dto: IOutgoingInvoiceBaseDto) {
        this.id = dto.id
        this.start = dayjs.utc(dto.start, "YYYY-MM-DD") // take only date ignoring the time
        this.end = dto.end ? dayjs.utc(dto.end, "YYYY-MM-DD") : undefined // take only date ignoring the time
        this.amount = dto.amount
        this.status = dto.status
        this.type = dto.type
        this.creditType = dto.creditType ?? undefined
        this.created = dayjs(dto.created)
        this.createdBy = dto.createdBy
        this.erpOrderNumber = dto.erpOrderNumber ?? undefined
        this.erpOrderNumberStatus = dto.erpOrderNumberStatus ?? undefined
        this.erpOrderNumberMessage = dto.erpOrderNumberMessage ?? undefined
        this.erpInvoiceNumber = dto.erpInvoiceNumber ?? undefined
        this.erpInvoiceNumberStatus = dto.erpInvoiceNumberStatus ?? undefined
        this.erpInvoiceDate = dto.erpInvoiceDate ? dayjs(dto.erpInvoiceDate, "YYYY-MM-DD") : undefined
        this.erpInvoiceDateStatus = dto.erpInvoiceDateStatus ?? undefined
        this.internalComment = dto.internalComment ?? undefined
        this.customer = dto.customer
        this.createdAutomatically = dto.createdAutomatically
        this.isAdHoc = dto.isAdHoc
        this.costUnit = dto.costUnit ?? undefined
    }

    printPeriod() {
        const start = this.start.format(i18n.t('config.dateLongFormat'))
        if (!this.end) {
            return start
        }
        return `${start} – ${this.end.format(i18n.t('config.dateLongFormat'))}`
    }

    getExchangeRateDate({ purpose, disposalDate }: { purpose: Purpose, disposalDate: Dayjs | null }): Dayjs | null {
        if (purpose !== Purpose.sale
            || this.type === OutgoingInvoiceType.fullCreditNote
            || this.type === OutgoingInvoiceType.partialCreditNote
            || this.type === OutgoingInvoiceType.partialDebitNote
            || this.isAdHoc
        ) {
            return this.created.clone().startOf("day")
        } else {
            return disposalDate
        }
    }
}



// OutgoingInvoice

export interface IOutgoingInvoiceDto extends IOutgoingInvoiceBaseDto {
    reservationId?: Guid | null
    residualAmount: number | null
    invoicePositions?: IGetReservationInvoiceInvoicePositionDto[] | null
    parentInvoiceIds?: Guid[] | null
    headerText?: string | null
    footerText?: string | null
    assets?: IGetInvoiceDraftAssetDto[] | null
    downtimePeriods?: Pick<IGetDowntimePeriodDto, "description" | "start" | "end" | "discount">[] | null
    downtimeDays?: number | null
    downtimeText?: string | null
    links: IApiLink[]
}

export class OutgoingInvoice extends OutgoingInvoiceBase {
    reservationId?: Guid
    creditedAmount: number
    residualAmount: number
    invoicePositions: ReservationInvoiceInvoicePosition[]
    parentInvoiceIds: Guid[]
    headerText?: string
    footerText?: string
    assets: InvoiceDraftAsset[]
    downtimePeriods: DowntimePeriod[]
    downtimeDays: number
    downtimeText?: string

    linkCancelInvoice?: IApiLink
    linkAddCreditNote?: IApiLink
    linkAddDebitNote?: IApiLink
    linkErpInvoiceSync?: IApiLink
    linkEditOrderNumber?: IApiLink
    linkDeleteInvoice?: IApiLink

    constructor(dto: IOutgoingInvoiceDto) {
        super(dto)
        this.reservationId = dto.reservationId ?? undefined
        this.residualAmount = dto.residualAmount ?? 0
        this.creditedAmount = this.amount - this.residualAmount
        this.invoicePositions = dto.invoicePositions?.map(ip => new ReservationInvoiceInvoicePosition(ip)) ?? []
        this.parentInvoiceIds = dto.parentInvoiceIds ?? []
        this.headerText = dto.headerText ?? undefined
        this.footerText = dto.footerText ?? undefined
        this.assets = dto.assets?.map(a => new InvoiceDraftAsset(a)) ?? []
        this.downtimePeriods = dto.downtimePeriods?.map(dp => new DowntimePeriod({ ...dp, id: "" })) ?? []
        this.downtimeDays = dto.downtimeDays ?? 0
        this.downtimeText = dto.downtimeText ?? undefined

        this.linkCancelInvoice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.fullCreditNote && lnk.rel === ApiLinkRel.new)
        this.linkAddCreditNote = dto.links.find(lnk => lnk.entity === ApiLinkEntity.partialCreditNote && lnk.rel === ApiLinkRel.new)
        this.linkAddDebitNote = dto.links.find(lnk => lnk.entity === ApiLinkEntity.partialDebitNote && lnk.rel === ApiLinkRel.new)
        this.linkErpInvoiceSync = dto.links.find(lnk => lnk.entity === ApiLinkEntity.erpInvoiceSync && lnk.rel === ApiLinkRel.new)
        this.linkEditOrderNumber = dto.links.find(lnk => lnk.entity === ApiLinkEntity.editInvoiceOrderNumber && lnk.rel === ApiLinkRel.all)
        this.linkDeleteInvoice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.deleteInvoice && lnk.rel === ApiLinkRel.all)
    }

    get rentalFeeAmount() {
        return this.invoicePositions.reduce((acc, ip) => acc + (ip.invoicePosition?.isRentalFee ? ip.amount : 0), 0)
    }
}


// OutgoingInvoiceItem

export interface IOutgoingInvoiceItemDto extends IOutgoingInvoiceBaseDto {
    reservationId: Guid
    requestNumber: string
    reservationEnd?: string | null
    purpose: Purpose
    customer: IOutgoingInvoiceCustomer
    assetId?: Guid | null
    lmid?: Lmid | null
    articleId: Guid
    articleDescription: string
    disposalDate?: string | null
    invoiceBeforeShipping: boolean
    links: IApiLink[]
}

export interface IOutgoingInvoiceItemAdditionalParams {
    createdByUserEmail: string
}

export class OutgoingInvoiceItem extends OutgoingInvoiceBase {
    reservationId: Guid
    requestNumber: string
    reservationEnd?: Dayjs
    purpose: Purpose
    assetId?: Guid
    lmid?: Lmid
    articleId: Guid
    articleDescription: string
    createdByUserEmail: string
    disposalDate?: Dayjs
    invoiceBeforeShipping: boolean
    linkResubmitInvoice?: IApiLink
    linkEditOrderNumber?: IApiLink
    linkDeleteInvoice?: IApiLink

    constructor(dto: IOutgoingInvoiceItemDto, params: IOutgoingInvoiceItemAdditionalParams) {
        super(dto)
        this.reservationId = dto.reservationId
        this.requestNumber = dto.requestNumber
        this.reservationEnd = dto.reservationEnd ? dayjs.utc(dto.reservationEnd) : undefined
        this.purpose = dto.purpose
        this.assetId = dto.assetId ?? undefined
        this.lmid = dto.lmid ?? undefined
        this.articleId = dto.articleId
        this.articleDescription = dto.articleDescription
        this.createdByUserEmail = params.createdByUserEmail
        this.disposalDate = dto.disposalDate ? dayjs(dto.disposalDate, "YYYY-MM-DD") : undefined
        this.invoiceBeforeShipping = dto.invoiceBeforeShipping
        this.linkResubmitInvoice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.resubmitInvoice && lnk.rel === ApiLinkRel.all)
        this.linkEditOrderNumber = dto.links.find(lnk => lnk.entity === ApiLinkEntity.editInvoiceOrderNumber && lnk.rel === ApiLinkRel.all)
        this.linkDeleteInvoice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.deleteInvoice && lnk.rel === ApiLinkRel.all)
    }
}


// ReservationOutgoingInvoice

export interface IReservationOutgoingInvoiceDto extends IOutgoingInvoiceBaseDto {
    parentInvoiceIds?: Guid[] | null
    invoicePositions?: IGetReservationOutgoingInvoiceInvoicePosition[] | null
    headerText?: string | null
    footerText?: string | null
    collectivelyCreditable?: boolean | null
    links: IApiLink[]
}

export class ReservationOutgoingInvoice extends OutgoingInvoiceBase {
    parentInvoiceIds: Guid[]
    invoicePositions: ReservationOutgoingInvoiceInvoicePosition[]
    headerText?: string
    footerText?: string
    collectivelyCreditable: boolean
    linkErpInvoiceSync?: IApiLink
    linkResubmitInvoice?: IApiLink
    linkEditOrderNumber?: IApiLink
    linkDeleteInvoice?: IApiLink

    constructor(dto: IReservationOutgoingInvoiceDto) {
        super(dto)
        this.parentInvoiceIds = dto.parentInvoiceIds ?? []
        this.invoicePositions = dto.invoicePositions?.map(ip => new ReservationOutgoingInvoiceInvoicePosition(ip)) ?? []
        this.headerText = dto.headerText ?? undefined
        this.footerText = dto.footerText ?? undefined
        this.collectivelyCreditable = dto.collectivelyCreditable ?? false
        this.linkErpInvoiceSync = dto.links.find(lnk => lnk.entity === ApiLinkEntity.erpInvoiceSync && lnk.rel === ApiLinkRel.new)
        this.linkResubmitInvoice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.resubmitInvoice && lnk.rel === ApiLinkRel.all)
        this.linkEditOrderNumber = dto.links.find(lnk => lnk.entity === ApiLinkEntity.editInvoiceOrderNumber && lnk.rel === ApiLinkRel.all)
        this.linkDeleteInvoice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.deleteInvoice && lnk.rel === ApiLinkRel.all)
    }

    get rentalFeeAmount() {
        return this.invoicePositions.reduce((acc, ip) => acc + (ip.invoicePosition?.isRentalFee ? ip.residualAmount : 0), 0)
    }

    get residualAmount() {
        return this.invoicePositions.reduce((acc, ip) => acc + ip.residualAmount, 0)
    }
}

// ReservationOutgoingInvoiceInvoicePosition

export interface IGetReservationOutgoingInvoiceInvoicePosition {
    reservationInvoicePositionId: Guid
    number: string
    amount: number
    residualAmount: number
    text?: string | null
}

export class ReservationOutgoingInvoiceInvoicePosition {
    reservationInvoicePositionId: Guid
    number: string
    amount: number
    residualAmount: number
    text?: string

    // filled separately
    invoicePosition?: TenantInvoicePosition

    constructor(dto: IGetReservationOutgoingInvoiceInvoicePosition) {
        this.reservationInvoicePositionId = dto.reservationInvoicePositionId
        this.number = dto.number
        this.amount = dto.amount
        this.residualAmount = dto.residualAmount
        this.text = dto.text ?? undefined
    }
}

// ReservationOutgoingInvoices

export interface IGetReservationOutgoingInvoicesDto {
    entities: IReservationOutgoingInvoiceDto[]
    links?: IApiLink[]
}

export class ReservationOutgoingInvoices {
    invoices: ReservationOutgoingInvoice[]
    canCreateAdHocInvoice: boolean
    linkCreateCollectiveCreditNote?: IApiLink

    constructor(dto: IGetReservationOutgoingInvoicesDto) {
        this.invoices = dto.entities.map(invDto => new ReservationOutgoingInvoice(invDto))
        this.canCreateAdHocInvoice = !!dto.links?.find(lnk => lnk.entity === ApiLinkEntity.adHocInvoiceDraft && lnk.rel === ApiLinkRel.self)
        this.linkCreateCollectiveCreditNote = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.collectiveCreditNote && lnk.rel === ApiLinkRel.new)
    }
}

// utils

export function getOutgoingInvoiceTypeLabel(type: OutgoingInvoiceType): string {
    return {
        [OutgoingInvoiceType.invoice]: i18n.t('outgoingInvoiceType.invoice'),
        [OutgoingInvoiceType.fullCreditNote]: i18n.t('outgoingInvoiceType.cancellation'),
        [OutgoingInvoiceType.partialCreditNote]: i18n.t('outgoingInvoiceType.creditNote'),
        [OutgoingInvoiceType.partialDebitNote]: i18n.t('outgoingInvoiceType.debitNote'),
        [OutgoingInvoiceType.collectiveCreditNote]: i18n.t('outgoingInvoiceType.collectiveCreditNote'),
    }[type] || type
}

export function getOutgoingInvoiceStatusLabel(status: OutgoingInvoiceStatus): string {
    return {
        [OutgoingInvoiceStatus.pendingErpSend]: i18n.t('outgoingInvoiceStatus.notSent'),
        [OutgoingInvoiceStatus.awaitImport]: i18n.t('outgoingInvoiceStatus.awaitImport'),
        [OutgoingInvoiceStatus.orderCreated]: i18n.t('outgoingInvoiceStatus.orderCreated'),
        [OutgoingInvoiceStatus.done]: i18n.t('outgoingInvoiceStatus.done'),
        [OutgoingInvoiceStatus.error]: i18n.t('outgoingInvoiceStatus.error'),
    }[status] || status
}

export async function handleCreditDebitNoteCreateError({
    e,
    closeCreateModal,
    onCustomerBlocked,
    onInvoiceBlocked,
}: {
    e: any
    closeCreateModal?: () => void
    onCustomerBlocked: () => void
    onInvoiceBlocked: () => void
}) {
    if (e instanceof CustomError && e.code === ErrorCode.conflict) {
        const {
            reservationIsInEdit,
            reservationIsInEditUserEmail,
            assetSplitOperationInProgress,
            blockedCustomer,
            invoiceBlocked,
            canceledInvoice,
            periodGap,
        } = (e as CustomError<ICreateInvoiceAndNoteConflictErrorPayload>).payload!

        if (canceledInvoice) {
            closeCreateModal?.()
            alertModals.show({
                title: i18n.t('common.attention'),
                buttonLabel: i18n.t('common.ok'),
                children: i18n.t('noteCreate.creditNoteAlreadyAdded'),
                maxWidth: "530px",
            })
        } else if (invoiceBlocked) {
            closeCreateModal?.()
            onInvoiceBlocked()
        } else if (blockedCustomer) {
            closeCreateModal?.()
            onCustomerBlocked()
        } else if (assetSplitOperationInProgress) {
            closeCreateModal?.()
            alertModals.show({
                title: i18n.t('common.attention'),
                buttonLabel: i18n.t('common.ok'),
                children: i18n.t('assetSplit.assetOccupiedBySplit'),
                maxWidth: "530px",
            })
        } else if (reservationIsInEdit) {
            closeCreateModal?.()
            alertModals.show({
                title: i18n.t('common.attention'),
                buttonLabel: i18n.t('common.ok'),
                children: i18n.t('reservation.lockedByDifferentUserX', {
                    email: reservationIsInEditUserEmail || "",
                    duration: EditSessionDuration.asMinutes() + EditSessionConfirmModalDuration.asMinutes()
                }),
                maxWidth: "586px",
            })
        } else if (periodGap) {
            console.log("Selected invoices have gaps in periods", e)
            toastMessages.showSystemError()
        }
        return
    }
    console.error(e)
    toastMessages.showSystemError()
}
